import {
  DatePicker,
  DatePickerPropsBase,
  IconButton,
  Popover,
  TextField,
  TextFieldProps,
} from 'omakase';
import { IconCalendar } from '@tock/icons';
import { LocalDate } from 'js-joda';
import { MouseEvent, useRef, useState } from 'react';
import { checkFormHasError, getFormHelperText } from '@tock/shared/ui/forms/formHelpers';
import { popoverRoot } from 'omakase/FormControls/formMultipleDatePickerCssDefs';
import { useField } from 'formik';

export type FormMultipleDatePickerField = LocalDate[];

type FormMultipleDatePickerFieldProps = Omit<TextFieldProps, 'name'> & {
  /**
   * Name of the field. Used as the key to access the value in Formik state.
   */
  name: string;
  /**
   * Pass through props to customize the date picker instance
   */
  DatePickerProps?: DatePickerPropsBase;
};

/**
 * Provides string label for the number of dates selected
 */
const getDisplayValue = (value: LocalDate[]) => {
  if (value.length === 0) {
    return '';
  }

  if (value.length === 1) {
    return '1 date selected';
  }

  return `${value.length} dates selected`;
};

/**
 * Formik field component for multiple date selection.
 *
 * @todo - reset button
 * @todo - visual QA/styling
 */
export const FormMultipleDatePickerField = ({
  name,
  helperText,
  label,
  id,
  DatePickerProps,
  ...rest
}: FormMultipleDatePickerFieldProps) => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>();
  const [field, meta, helpers] = useField<LocalDate[]>(name);
  const textInputRef = useRef();

  const hasError = checkFormHasError(meta);
  const helperTextValue = getFormHelperText(hasError, meta, helperText);
  const displayValue = getDisplayValue(field.value);

  const handleDatesSelected = (selectedDates: LocalDate[]) => {
    helpers.setValue(selectedDates);
  };

  const handleOpenPopover = (e: MouseEvent<HTMLElement>) => {
    setAnchorEl(textInputRef.current);
  };

  const handleClosePopover = () => {
    anchorEl?.focus(); // return focus to the triggering element
    setAnchorEl(null);
  };

  return (
    <>
      <TextField
        error={hasError}
        helperText={helperTextValue}
        id={id}
        inputProps={{ readOnly: true }}
        value={displayValue}
        label={label}
        ref={textInputRef}
        endAdornment={
          <IconButton
            onClick={handleOpenPopover}
            size="small"
            variant="ghost"
            type="button"
            aria-label={`${label}. Open calendar date picker`}
          >
            <IconCalendar />
          </IconButton>
        }
        {...rest}
      />
      <Popover
        anchorEl={anchorEl}
        open={!!anchorEl}
        onClose={handleClosePopover}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        PaperProps={{
          role: 'dialog',
          'aria-label': 'Choose date(s)',
        }}
        maxWidthPx={400}
        css={popoverRoot}
      >
        <DatePicker
          selectionType="multiple"
          onDatesSelected={handleDatesSelected}
          selectedDates={field.value}
          {...DatePickerProps}
        />
      </Popover>
    </>
  );
};
